/**
 * Nepali locale for devices-ui.
 * Export is the "devices" subtree: host apps merge this into their i18n
 * resources as translation.devices so that t('devices.list.heading') etc. resolve.
 */
import type { DevicesLocale } from './en'

export const devicesNe = {
  // ---------------------------------------------------------------------------
  // Common / shared labels
  // ---------------------------------------------------------------------------
  common: {
    cancel: `रद्द गर्नुहोस्`,
    delete: `मेटाउनुहोस्`,
    save: `सुरक्षित गर्नुहोस्`,
    saveAndReboot: `सुरक्षित गर्नुहोस् र पुनः सुरु गर्नुहोस्`,
    actions: `कार्यहरू`,
    error: `त्रुटि`,
    yes: `हो`,
    no: `होइन`,
    all: `सबै`,
    unchanged: `अपरिवर्तित`,
    loading: `लोड हुँदैछ...`,
    none: `— कुनै पनि छैन —`,
    backToList: `सूचीमा फर्कनुहोस्`,
  },

  // ---------------------------------------------------------------------------
  // Options used across multiple components
  // ---------------------------------------------------------------------------
  options: {
    transportUdp: `UDP`,
    transportTcp: `TCP`,
    transportTls: `TLS`,
    jitterNormal: `सामान्य`,
    jitterLow: `कम`,
    jitterHigh: `उच्च`,
    natNormal: `सामान्य`,
    natStun: `STUN`,
    serverGroupPrimary: `प्राथमिक`,
    serverGroupSecondary: `द्वितीयक`,
    sourceMango: `Mango नयाँ`,
    sourceRefurbished: `Mango पुनर्निर्मित`,
    sourceCustomer: `ग्राहक`,
    wifiSecurityNone: `NONE`,
    wifiSecurityWep: `WEP`,
    wifiSecurityWpa: `WPA/WPA2 PSK`,
    wifiSecurityWpa3: `WPA3-Personal`,
    timezoneAlaska: `Alaska`,
    timezoneArizona: `Arizona`,
    timezoneAtlantic: `Atlantic Time (Canada)`,
    timezoneCentral: `Central Time (US & Canada)`,
    timezoneEastern: `Eastern Time (US & Canada)`,
    timezoneGreenwich: `Greenwich Mean Time (UK)`,
    timezoneHawaii: `Hawaii`,
    timezoneIndiana: `Indiana (East)`,
    timezoneMountain: `Mountain Time (US & Canada)`,
    timezonePacific: `Pacific Time (US & Canada)`,
    timezonePuertoRico: `Puerto Rico`,
  },

  // ---------------------------------------------------------------------------
  // Device form (add / edit)
  // ---------------------------------------------------------------------------
  form: {
    addDevice: `यन्त्र थप्नुहोस्`,
    editDevice: `यन्त्र सम्पादन गर्नुहोस्`,
    createDevice: `यन्त्र सिर्जना गर्नुहोस्`,
    loadingDevice: `यन्त्र लोड हुँदैछ...`,

    // Section headers
    deviceInfo: `यन्त्र जानकारी`,
    advanced: `उन्नत`,
    displaySettings: `प्रदर्शन सेटिङहरू`,

    // Field labels
    model: `मोडेल`,
    selectModel: `यन्त्र मोडेल छान्नुहोस्…`,
    mostCommonYealink: `सबैभन्दा सामान्य (Yealink)`,
    macAddress: `MAC ठेगाना`,
    macPlaceholder: `AA:BB:CC:DD:EE:FF`,
    generateRandomMac: `अनियमित MAC ठेगाना उत्पन्न गर्नुहोस्`,
    generateRandomMacConfirmTitle: `MAC ठेगाना प्रतिस्थापन गर्ने?`,
    generateRandomMacConfirmDescription: `अनियमित MAC ठेगाना उत्पन्न गर्दा हालको मान प्रतिस्थापित हुनेछ। के तपाईं जारी राख्न निश्चित हुनुहुन्छ?`,
    generateRandomMacConfirmAction: `नयाँ MAC उत्पन्न गर्नुहोस्`,
    copyProvisioningMac: `प्रोभिजनिङ URL का लागि MAC प्रतिलिपि गर्नुहोस्`,
    copyProvisioningMacHint: `सानो अक्षरमा, कोलनबिना प्रतिलिपि गर्नुहोस् (प्रोभिजनिङ URL का लागि)`,
    copyProvisioningMacCopied: `{{value}} प्रतिलिपि गरियो`,
    copyProvisioningMacFailed: `प्रतिलिपि असफल भयो। मानलाई आफैं चयन गर्नुहोस्।`,
    checkingUniqueness: `अद्वितीयता जाँच गर्दै…`,
    macRequired: `MAC ठेगाना आवश्यक छ`,
    macInvalid: `MAC ठेगाना अमान्य छ`,
    macAlreadyInUse: `MAC ठेगाना पहिले नै प्रयोगमा छ`,
    modelRequired: `यन्त्र मोडेल आवश्यक छ।`,
    warrantyExpiresAt: `वारेन्टी समाप्त हुने मिति`,
    source: `स्रोत`,
    lastProvisionedTime: `अन्तिम प्रावधान समय`,
    lastProvisionedFrom: `अन्तिम प्रावधान स्थान`,
    assignedUser: `नियुक्त प्रयोगकर्ता`,
    assignedUserRequired: `नियुक्त प्रयोगकर्ता आवश्यक छ।`,
    directory: `निर्देशिका`,
    directoryRequired: `निर्देशिका आवश्यक छ।`,
    selectDirectory: `निर्देशिका छान्नुहोस्...`,
    primaryLineExtension: `प्राथमिक लाइन विस्तार`,
    noExtensionsForUser: `नियुक्त प्रयोगकर्तासँग कुनै मान्य लाइन विस्तार छैन। बचत गर्नु अघि अर्को प्रयोगकर्ता छान्नुहोस् वा लाइन विस्तार तोक्नुहोस्।`,
    primaryLineExtensionRequired: `प्राथमिक लाइन विस्तार आवश्यक छ।`,
    timeZone: `समय क्षेत्र`,
    stationName: `स्टेशन नाम`,
    stationNameRequired: `स्टेशन नाम आवश्यक छ।`,
    stationNamePlaceholder: `जस्तै: अगाडि डेस्क`,
    enableDaylightSavings: `दिवा बचत सक्षम गर्नुहोस्`,
    cannotSaveTitle: `अहिले बचत गर्न सकिँदैन`,
    buttonConfigurationInvalid: `बचत गर्नु अघि अमान्य बटन कन्फिगरेसन सच्याउनुहोस्।`,

    // Advanced section labels
    pages: `पृष्ठहरू`,
    lineKeysAvailable: `लाइन कुञ्जीहरू उपलब्ध`,
    selectModelFirst: `पहिले मोडेल छान्नुहोस्...`,
    lineKeysHelper: `({{pages}} पृष्ठ{{pageSuffix}} x {{keysPerPage}} कुञ्जीहरू = {{total}} कुल)`,
    allowProvisioning: `प्रावधान अनुमति दिनुहोस्`,
    sidecarEnabled: `साइडकार सक्षम`,
    transport: `ट्रान्सपोर्ट`,
    jitter: `जिटर`,
    serverGroupHost: `सर्भर समूह होस्ट`,
    natTraversal: `NAT ट्राभर्सल`,
    wifiEnabled: `WiFi सक्षम`,
    wifiNetworkSsid: `WiFi नेटवर्क SSID`,
    wifiSecurityMode: `WiFi सुरक्षा मोड`,
    wifiPassword: `WiFi पासवर्ड`,
    acousticShieldEnabled: `ध्वनिक ढाल सक्षम`,
  },

  // ---------------------------------------------------------------------------
  // Device preview card
  // ---------------------------------------------------------------------------
  preview: {
    title: `यन्त्र पूर्वावलोकन`,
    unavailable: `चयन गरिएको मोडेलको लागि पूर्वावलोकन उपलब्ध छैन।`,
    emptyAlt: `खाली यन्त्र पूर्वावलोकन`,
  },

  // ---------------------------------------------------------------------------
  // Button grid
  // ---------------------------------------------------------------------------
  buttons: {
    linesAndButtons: `लाइनहरू र बटनहरू`,
    sidecarButtons: `साइडकार बटनहरू`,
    lineButtonHelper_one: `{{modelName}} का लागि {{count}} बटन स्लट। पङ्क्ति १ सधैँ लाइन हुन्छ र प्राथमिक लाइन विस्तारलाई पछ्याउँछ।`,

    lineButtonHelper_other: `{{modelName}} का लागि {{count}} बटन स्लटहरू। पङ्क्ति १ सधैँ लाइन हुन्छ र प्राथमिक लाइन विस्तारलाई पछ्याउँछ।`,
    sidecarButtonHelper_one: `{{count}} साइडकार स्लट।`,

    sidecarButtonHelper_other: `{{count}} साइडकार स्लटहरू।`,
    noSlotsForModel: `यो मोडेलको लागि बटन स्लटहरू उपलब्ध छैन।`,
    selectModelForSlots: `बटन स्लटहरू हेर्न यन्त्र मोडेल छान्नुहोस्।`,
    columnNumber: `#`,
    columnFunction: `प्रकार्य`,
    columnValue: `मान`,
    columnLabel: `लेबल`,
    phoneNumberPlaceholder: `फोन नम्बर`,
    optionalLabelPlaceholder: `वैकल्पिक लेबल`,
    extensionNone: `-- कुनै पनि छैन --`,

    // Button function options
    functionBlank: `खाली`,
    functionLine: `लाइन`,
    functionRollover: `रोलओभर`,
    functionSpeedDial: `स्पीड डायल`,
    functionPresence: `BLF / उपस्थिति`,
    functionParkingLot: `पार्किंग लट`,
    functionSharedVoicemail: `साझा भ्वाइसमेल`,

    // Preset speed dial options
    presetMangoSupport: `Mango समर्थन`,
    presetLoginQueue: `लगइन कतार`,
    presetLogoutQueue: `लगआउट कतार`,
    presetBlockCaller: `कलर ब्लक गर्नुहोस्`,
    presetOpenClose: `खोल्नुहोस्/बन्द गर्नुहोस्`,
    presetPauseResumeRecording: `रेकर्डिङ रोक्नुहोस्/पुनः सुरु गर्नुहोस्`,
    presetAlternateVm: `वैकल्पिक VM`,
    preset911History: `911 इतिहास`,

    // Preset button labels
    presetLabelMangoSupport: `Mango Support`,
    presetLabelLoginQ: `Q लगइन`,
    presetLabelLogoutQ: `Q लगआउट`,
    presetLabelBlock: `ब्लक`,
    presetLabelOpenClose: `खुला/बन्द`,
    presetLabelPauseResume: `पज/पुनः सुरु`,
    presetLabelAltVm: `वैकल्पिक VM`,
    presetLabel911Hist: `911 इतिहास`,

    parkingLotOption: `पार्किंग लट {{number}}`,

    multipleLineWarningTitle: `धेरै लाइन बटनहरू पत्ता लागे`,
    multipleLineWarning: `धेरै लाइन बटनहरू थप्दा अनपेक्षित परिणामहरू आउन सक्छन्। तपाईंले छान्नुभएको विस्तार अर्को लाइन बटनमा तोकिएको छैन र अन्य कुनै यन्त्रमा कन्फिगर गरिएको छैन भनी सुनिश्चित गर्नुहोस्।`,
    multipleLineWarningProceed: `अगाडि बढ्नुहोस्`,
    multipleLineWarningCancel: `रद्द गर्नुहोस्`,
  },

  // ---------------------------------------------------------------------------
  // Clone dialog
  // ---------------------------------------------------------------------------
  clone: {
    title: `यन्त्र क्लोन गर्नुहोस्`,
    description: `"{{sourceLabel}}" बाट नयाँ यन्त्र सिर्जना गर्नुहोस्। एक अद्वितीय MAC ठेगाना प्रविष्ट गर्नुहोस्। सबै सेटिङहरू र बटनहरू नयाँ यन्त्रमा प्रतिलिपि गरिनेछ।`,
    loadingSettings: `यन्त्र सेटिङहरू लोड हुँदैछ…`,
    stationLabelPlaceholder: `यन्त्रको क्लोन`,
    cloneButton: `यन्त्र क्लोन गर्नुहोस्`,
  },

  // ---------------------------------------------------------------------------
  // Delete dialog
  // ---------------------------------------------------------------------------
  deleteDialog: {
    title: `यन्त्र मेटाउनुहोस्`,
    confirmation: `के तपाईं "{{name}}" मेटाउन निश्चित हुनुहुन्छ? यो कार्य पूर्ववत गर्न सकिँदैन।`,
    unnamedDevice: `नामहीन यन्त्र`,
  },

  // ---------------------------------------------------------------------------
  // Mass edit dialog
  // ---------------------------------------------------------------------------
  massEdit: {
    title: `बहु यन्त्र सम्पादन`,
    description_one: `{{count}} यन्त्र अपडेट गर्दै। हालको मान राख्न फिल्ड परिवर्तन नगरी छोड्नुहोस्।`,

    description_other: `{{count}} यन्त्रहरू अपडेट गर्दै। हालको मान राख्न फिल्ड परिवर्तन नगरी छोड्नुहोस्।`,
    transport: `ट्रान्सपोर्ट`,
    natTraversal: `NAT ट्राभर्सल`,
    jitter: `जिटर`,
    timeZone: `समय क्षेत्र`,
    daylightSaving: `दिवा बचत`,
    allowProvisioning: `प्रावधान अनुमति दिनुहोस्`,
    serverGroupHost: `सर्भर समूह होस्ट`,
    saveButton_one: `{{count}} यन्त्र सुरक्षित गर्नुहोस्`,

    saveButton_other: `{{count}} यन्त्रहरू सुरक्षित गर्नुहोस्`,
  },

  // ---------------------------------------------------------------------------
  // Set desired firmware dialog
  // ---------------------------------------------------------------------------
  setFirmware: {
    title: `इच्छित फर्मवेयर संस्करण सेट गर्नुहोस्`,
    description: `{{count}} चयन गरिएको यन्त्र(हरू)को लागि इच्छित फर्मवेयर संस्करण सेट गर्नुहोस्। अद्यावधिक पछि कन्फिगरेसन पुनः लोड गरिनेछ।`,
    label: `इच्छित फर्मवेयर संस्करण`,
    placeholder: `जस्तै: 1.2.3.4`,
    confirmButton: `सेट गर्नुहोस् र पुनः लोड गर्नुहोस्`,
  },

  // ---------------------------------------------------------------------------
  // List page
  // ---------------------------------------------------------------------------
  list: {
    heading: `यन्त्रहरू`,
    searchPlaceholder: `नाम, प्रयोगकर्ता, विस्तार, वा MAC द्वारा खोज्नुहोस्...`,
    addDevice: `यन्त्र थप्नुहोस्`,
    actionsButton: `कार्यहरू`,
    actionsWithCount: `कार्यहरू ({{count}} चयन गरिएको)`,
    massEditAction: `बहु सम्पादन ({{count}})`,
    reloadConfigAction: `कन्फिगरेसन पुनः लोड गर्नुहोस् ({{count}})`,
    reloadConfigRebootAction: `कन्फिग पुनः लोड र पुनः सुरु गर्नुहोस् ({{count}})`,
    setFirmwareAction: `इच्छित फर्मवेयर सेट गर्नुहोस् ({{count}})`,
    rebuildAll: `सबै पुनर्निर्माण गर्नुहोस्`,
    rebuildAndReboot: `पुनर्निर्माण र पुनः सुरु गर्नुहोस्`,
    rebuildAllConfirmTitle: `सबै यन्त्रका कन्फिगरेसन पुनर्निर्माण गर्ने?`,
    rebuildAllConfirmDescription: `तपाईं यो पीबीएक्सका सबै यन्त्रहरूको कन्फिगरेसन पुनर्निर्माण गर्न लाग्नुभएको छ। के तपाईं जारी राख्न निश्चित हुनुहुन्छ?`,
    rebuildAllConfirmAction: `सबै पुनर्निर्माण गर्नुहोस्`,
    rebuildAndRebootConfirmTitle: `सबै यन्त्रहरू पुनर्निर्माण र पुनः सुरु गर्ने?`,
    rebuildAndRebootConfirmDescription: `तपाईं यो पीबीएक्सका सबै यन्त्रहरूको कन्फिगरेसन पुनर्निर्माण गरेर पुनः सुरु गर्न लाग्नुभएको छ। के तपाईं जारी राख्न निश्चित हुनुहुन्छ?`,
    rebuildAndRebootConfirmAction: `पुनर्निर्माण र पुनः सुरु गर्नुहोस्`,
    pbxRequired: `यन्त्र पृष्ठको लागि पीबीएक्स आवश्यक छ।`,
    failedToLoad: `यन्त्रहरू लोड गर्न असफल`,
    noMatchingDevices: `तपाईंको खोज मापदण्डसँग मिल्ने कुनै यन्त्रहरू छैनन्`,

    // Column headers
    columnName: `नाम`,
    columnPrimaryExt: `प्राथमिक विस्तार`,
    columnUser: `प्रयोगकर्ता`,
    columnModel: `मोडेल`,
    columnMacAddress: `MAC ठेगाना`,
    columnFirmware: `फर्मवेयर`,
    columnWarranty: `वारेन्टी`,
    columnLastCheckIn: `अन्तिम जाँच`,
    columnInitializedOn: `सुरु गरिएको मिति`,
    columnConfiguration: `कन्फिगरेसन`,
    columnActions: `कार्यहरू`,
    selectAllDevices: `सबै यन्त्रहरू चयन गर्नुहोस्`,
  },

  // ---------------------------------------------------------------------------
  // Device row
  // ---------------------------------------------------------------------------
  row: {
    loadingDevices: `यन्त्रहरू लोड हुँदैछ...`,
    noDevices: `कुनै यन्त्रहरू छैनन्`,
    noDevicesDescription: `यो पीबीएक्सको लागि अझै कुनै यन्त्रहरू छैनन्।`,
    selectDevice: `{{name}} चयन गर्नुहोस्`,
    editDevice: `{{name}} सम्पादन गर्नुहोस्`,
    modelFallback: `मोडेल {{id}}`,
    reportsModel: `रिपोर्ट: {{model}}`,
    warrantyInWarranty: `वारेन्टीमा`,
    warrantyExpired: `समाप्त भएको`,
    warrantyUnknown: `वारेन्टी छैन`,
    changesPending: `परिवर्तनहरू बाँकी`,
    sidecar: `साइडकार`,
    noProv: `प्रावधान छैन`,
    fromSource: `{{source}} बाट`,
    desiredFirmware: `(इच्छित: {{version}})`,
    firmwareUnknown: `अज्ञात`,
    rowActions: `पङ्क्ति कार्यहरू`,
    cloneAction: `क्लोन गर्नुहोस्`,
    deleteAction: `मेटाउनुहोस्`,
  },

  // ---------------------------------------------------------------------------
  // Filters
  // ---------------------------------------------------------------------------
  filters: {
    make: `निर्माता`,
    model: `मोडेल`,
    firmware: `फर्मवेयर`,
    firmwarePlaceholder: `कुनै पनि`,
    warranty: `वारेन्टी`,
    transport: `ट्रान्सपोर्ट`,
    jitter: `जिटर`,
    sidecar: `साइडकार`,
    provisioning: `प्रावधान`,
    clearFilters: `फिल्टरहरू खाली गर्नुहोस्`,
    warrantyUnknown: `वारेन्टी छैन`,
    warrantyInWarranty: `वारेन्टीमा`,
    warrantyExpired: `समाप्त भएको`,
    provisioningEnabled: `सक्षम`,
    provisioningDisabled: `अक्षम`,
    provisioningChangesPending: `परिवर्तनहरू बाँकी`,
    toggleFilters: `फिल्टरहरू टगल गर्नुहोस्`,
  },

  // ---------------------------------------------------------------------------
  // Pagination / toolbar
  // ---------------------------------------------------------------------------
  pagination: {
    resultsPerPage: `प्रति पृष्ठ परिणामहरू:`,
    totalCount: `({{count}} कुल)`,
    previousPage: `अघिल्लो पृष्ठ`,
    nextPage: `अर्को पृष्ठ`,
    all: `सबै`,
    jumpToPage: `पृष्ठ {{page}} मा जानुहोस्`,
  },

  // ---------------------------------------------------------------------------
  // Toast messages
  // ---------------------------------------------------------------------------
  toast: {
    deviceCreated: `यन्त्र सिर्जना गरियो`,
    createFailed: `यन्त्र सिर्जना गर्न असफल`,
    deviceUpdated: `यन्त्र अद्यावधिक गरियो`,
    deviceUpdatedRebooting: `यन्त्र अद्यावधिक र पुनः सुरु हुँदैछ`,
    updateFailed: `यन्त्र अद्यावधिक गर्न असफल`,
  },
} as const satisfies DevicesLocale
